import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ManagementTabsService } from '@app/management-tabs/services/management-tabs.service';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { ActionType, IdpName, Tenant, PrivilegeAccess, ExternalRoles } from '@app/shared/enums';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { RoleService } from '@app/shared/services/api/role.service';
import { UserService } from '@app/shared/services/api/user.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { VirusScannerService } from '@app/shared/services/virus-scanner.service';
import { UserProfile } from '@app/user-list/models/user-profile';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { forEach, isNil } from 'lodash';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { DeleteAccountDialogComponent } from './dialog/delete-account-dialog/delete-account-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, forkJoin, throwError } from 'rxjs';
import { catchError, first, switchMap, takeUntil } from 'rxjs/operators';
import { User } from '@app/shared/models/user';
import { Municipality } from '@app/shared/services/api/municipality.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';

@Component({
  selector: 'app-my-personal-data',
  templateUrl: './my-personal-data.component.html',
  styleUrls: ['./my-personal-data.component.scss'],
})
export class MyPersonalDataComponent implements OnDestroy, OnInit {
  private unsubscribe$ = new Subject<void>();
  isKam = this.authService.isKAM;
  isEdit = false;
  user = {} as UserProfile;
  imageSrc: any;
  guid: string = '';
  isImageLoading = false;
  saveErrorText = '';
  telefonRegex = '';
  roleName: string = '';
  roleCode: string = '';
  errorMmessagePic = false;
  orientation = 'landscape';
  idpName: string = '';
  hasMalfunctionNotifStatusCb: boolean = false;
  hasAllowToRcvWorkOrderEmailsCb: boolean = false;
  userMunicipalities!: Municipality[];
  isExternalUser!: boolean;

  imageUploader: FileUploader = new FileUploader({
    maxFileSize: 1 * 1024 * 1024,
    allowedMimeType: ['image/jpeg', 'image/jpg', 'image/png'],
    queueLimit: 1,
    url: '',
  });

  editForm = new FormGroup({
    firstname: new FormControl('', [Validators.required]),
    lastname: new FormControl('', [Validators.required]),
    telefon: new FormControl('', [Validators.required]),
    mobile: new FormControl(''),
    fax: new FormControl(''),
    allowToReceiveWorkOrderEmails: new FormControl({
      value: false,
      disabled: false,
    }),
    allowNewsletter: new FormControl({
      value: false,
      disabled: false,
    }),
    malFunctionNotificationStatus: new FormControl({ value: false, disabled: false }),
    receiveDocumentEmailStatus: new FormControl({ value: false, disabled: false }),
  });

  constructor(
    public authService: AuthService,
    public matDialog: MatDialog,
    public overlayLoaderService: OverlayLoaderService,
    public translate: TranslateService,
    public managementTabsService: ManagementTabsService,
    private userService: UserService,
    private roleService: RoleService,
    private scannerService: VirusScannerService,
    private azureBlob: AzureBlobStorageService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  ngOnInit() {
    this.isEdit = false;
    this.hasMalfunctionNotifStatusCb = !this.isKam && !this.authService.isDsoUser && environment.tenant === Tenant.BAYERNWERK;
    this.hasAllowToRcvWorkOrderEmailsCb = this.authService.isAdminAreaAndMunicipalityWorker && this.authService.permissions.SOCKET_SERVICE;
    this.isExternalUser = Object.values(ExternalRoles).includes(this.authService.currentUser.roleCode as ExternalRoles);
    this.translate.get('module.userProfile.saveErrorText').subscribe((val) => (this.saveErrorText = val));
    this.getUser();

    this.imageUploader.onWhenAddingFileFailed = this.onWhenAddingFileFailed.bind(this);
    this.imageUploader.onAfterAddingFile = this.onAfterAddingVideo.bind(this);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.managementTabsService.error = '';
  }

  onWhenAddingFileFailed() {
    this.isImageLoading = false;
    this.errorMmessagePic = true;
  }

  onAfterAddingVideo(item: FileItem) {
    this.isImageLoading = true;
    const reader = new FileReader();
    reader.readAsDataURL(item._file);
    let img: string | ArrayBuffer | null;
    reader.onload = (_event) => {
      img = reader.result;
    };

    const filePath = `user-profile/${new Date().getTime()}/${item._file.name}`;

    this.azureBlob
      .scanBlob(filePath, item.file.rawFile as File, item.file.name as string)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.isImageLoading = false;
          this.imageUploader.clearQueue();
          this.scannerService.errorChecker(err);
          return throwError(() => err);
        }),
        switchMap(() =>
          this.userService.uploadImage(this.user.guid, `assets/images/users/${item.file.name}`, item.file.rawFile as File, item.file.name as string)
        ),
        first()
      )
      .subscribe({
        next: () => {
          this.imageSrc = img;
          this.isImageLoading = false;
          this.errorMmessagePic = false;
          this.imageUploader.clearQueue();
        },
        error: () => {
          this.errorMmessagePic = true;
          this.imageUploader.clearQueue();
        },
      });
  }

  private loadDetails(u: User) {
    this.guid = u.guid;
    this.user.email = u.email;
    this.user.firstname = u.firstname;
    this.user.imageName = u.imageName;
    this.user.lastname = u.lastname;
    this.user.malFunctionNotificationStatus = u.malFunctionNotificationStatus === '1';
    this.user.allowToReceiveNewDocumentsEmails = u.allowToReceiveNewDocumentsEmails;
    this.user.telefon = !isNil(u.telefon) ? u.telefon.replace('&#x2B;', '+') : '';
    this.user.guid = u.guid;
    this.user.mobile = u.mobil === null ? '' : u.mobil;
    this.user.fax = u.fax === null ? '' : u.fax;
    this.user.isPriviledgedAccess = u.isPriviledgedAccess ? PrivilegeAccess.YES : PrivilegeAccess.NO;
    this.roleService.getById(u.roleId).subscribe((r) => {
      if (r.length > 0) {
        this.roleName = r[0].name;
        this.roleCode = r[0].code;
      }
    });

    this.idpName = this.authService.currentUser.idpName.toLocaleUpperCase();
    this.editForm.patchValue(this.user);
  }

  getUser() {
    this.overlayLoaderService.isLoading = true;
    this.userService.getByUserGuid(this.authService.currentUser.guid).subscribe({
      next: (u) => {
        this.loadDetails(u);
        if (u.imageName) {
          this.isImageLoading = true;
          this.azureBlob.getBlobImageSafeUrl(`${environment.tenant}/assets/images/users/${u.imageName}`).subscribe(
            (result: any) => {
              this.imageSrc = result;
              this.isImageLoading = false;
            },
            () => {
              this.isImageLoading = false;
            }
          );
        }
        this.editForm.controls['receiveDocumentEmailStatus'].setValue(u.allowToReceiveNewDocumentsEmails);
        this.editForm.controls['allowToReceiveWorkOrderEmails'].setValue(u.allowToReceiveWorkOrderEmails ?? null);
        this.editForm.controls['allowNewsletter'].setValue(u.allowNewsletter === true);
      },
      error: () => (this.overlayLoaderService.isLoading = false),
      complete: () => (this.overlayLoaderService.isLoading = false),
    });
  }

  onEdit() {
    this.isEdit = true;
    this.editForm.controls['telefon'].setValidators([
      Validators.required,
      Validators.pattern(this.translate.instant('module.userProfile.myPersonalData.telefonRegex')),
    ]);
    this.editForm.controls['mobile'].setValidators([
      Validators.required,
      Validators.pattern(this.translate.instant('module.userProfile.myPersonalData.telefonRegex')),
    ]);
    this.editForm.controls['fax'].setValidators([
      Validators.required,
      Validators.pattern(this.translate.instant('module.userProfile.myPersonalData.telefonRegex')),
    ]);
  }

  onCancel() {
    this.isEdit = false;
    this.getUser();
  }

  onSave() {
    this.isEdit = false;
    this.managementTabsService.error = '';
    const firstname = this.editForm.controls.firstname.valid;
    const lastname = this.editForm.controls.lastname.valid;
    const telefon = this.editForm.controls.telefon.valid;
    const mobile = this.editForm.controls.mobile.valid;
    const fax = this.editForm.controls.fax.valid;
    const user = {
      firstName: this.editForm.value.firstname,
      lastName: this.editForm.value.lastname,
      telefon: this.removeTrailingSpaces(this.editForm.value.telefon ?? ''),
      mobil: this.editForm.value.mobile ?? undefined,
      fax: this.editForm.value.fax ?? undefined,
      malFunctionNotificationStatus: this.editForm.controls['malFunctionNotificationStatus'].value ? '1' : '0',
      allowToReceiveWorkOrderEmails: this.editForm.controls['allowToReceiveWorkOrderEmails'].value ?? undefined,
      allowNewsletter: this.editForm.controls['allowNewsletter'].value ?? undefined,
      guid: this.authService.currentUser.guid,
      allowToReceiveNewDocumentsEmails: this.editForm.controls['receiveDocumentEmailStatus'].value ?? undefined,
    };

    if (
      firstname &&
      lastname &&
      telefon &&
      (this.editForm.get('mobile')?.value === '' ? this.editForm.get('mobile')?.value === '' : mobile) &&
      (this.editForm.get('fax')?.value === '' ? this.editForm.get('fax')?.value === '' : fax)
    ) {
      this.overlayLoaderService.isLoading = true;
      this.userService
        .patch(this.user.guid, user)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (u: User) => {
            if (this.authService.currentUser.idpName.toLocaleUpperCase() === IdpName.CIAM) {
              this.userService
                .createServiceBusPayload({
                  ...u,
                  idpname: this.idpName,
                  rolesCode: this.roleCode,
                  municipalities: this.userMunicipalitiesService.municipalities.map((municipality) => ({
                    name: municipality.name,
                    municipalityKey: municipality.key,
                  })),
                  action: ActionType.update,
                })
                .pipe(
                  switchMap((payload) =>
                    forkJoin([
                      this.userService.postMessageToServiceBus(this.authService.currentUser.userId, payload),
                      this.userService.updateCIAMUser(this.authService.currentUser.userId, payload),
                    ])
                  ),
                  takeUntil(this.unsubscribe$)
                )
                .subscribe({
                  next: () => {
                    this.reloadUser(u);
                    this.showSuccessPopup();
                    this.overlayLoaderService.isLoading = false;
                  },
                  error: () => {
                    this.reloadUser(u);
                    this.overlayLoaderService.isLoading = false;
                    this.managementTabsService.error = this.saveErrorText;
                  },
                });
            } else {
              this.userService
                .createServiceBusPayload({
                  ...u,
                  idpname: this.idpName,
                  rolesCode: 'eon_internal',
                  municipalities: [],
                  action: ActionType.update,
                })
                .pipe(
                  switchMap((payload) => forkJoin([this.userService.postMessageToServiceBus(this.authService.currentUser.userId, payload)])),
                  takeUntil(this.unsubscribe$)
                )
                .subscribe({
                  next: () => {
                    this.reloadUser(u);
                    this.showSuccessPopup();
                    this.overlayLoaderService.isLoading = false;
                  },
                  error: () => {
                    this.reloadUser(u);
                    this.overlayLoaderService.isLoading = false;
                    this.managementTabsService.error = this.saveErrorText;
                  },
                });
            }
          },
          error: () => {
            this.overlayLoaderService.isLoading = false;
            this.managementTabsService.error = this.saveErrorText;
          },
        });
    }
  }

  reloadUser(u: User) {
    const user = u;
    this.authService.updateCurrentUser(
      user.firstname,
      user.lastname,
      user.telefon,
      user.mobil,
      user.fax,
      user.malFunctionNotificationStatus,
      user.allowToReceiveNewDocumentsEmails
    );
    this.loadDetails(user);
  }

  showSuccessPopup() {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'Benutzerprofil wurde erfolgreich aktualisiert',
        orientation: 'row',
        alignment: 'start',
      },
      panelClass: ['-md'],
    });
  }

  changePassword() {
    const changePasswordUrl = environment.ciamUrlPasswordReset.replace('{email}', this.user.email).replace('{appid}', environment.ciamAppExternalId);
    this.overlayLoaderService.isLoading = true;

    window.location.href = changePasswordUrl;
  }

  removeTrailingSpaces(string: string) {
    let result = '';
    const splitString = string.split(' ');

    forEach(splitString, (str) => {
      const ss = str.trim();
      if (!isNil(ss) && ss !== '') {
        result = result + ss + ' ';
      }
    });

    return result.replace(/\s+/g, '').trim();
  }

  removeUserInputSpaces(controlName: string): void {
    const inputNumber = (this.editForm.controls as any)[controlName];

    if (inputNumber) {
      const cleanedInputNumber = this.removeTrailingSpaces(inputNumber.value);
      inputNumber.setValue(cleanedInputNumber);
    }
  }

  onImageLoad(event: any) {
    const imgHeight = event.target.clientHeight;
    const imgWidth = event.target.clientWidth;

    if (imgHeight >= imgWidth) {
      this.orientation = 'portrait';
    } else {
      this.orientation = 'landscape';
    }
  }

  isCIAM() {
    return this.idpName === IdpName.CIAM;
  }

  deleteUserAccount() {
    this.matDialog.open(DeleteAccountDialogComponent, {
      panelClass: ['-md', 'delete-user-dialog'],
      autoFocus: false,
    });
  }
}
